import { ChangeEvent, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, SearchInput } from '@ds';
import { MdOutlineAdd } from 'react-icons/md';
import { AdminPageHeader, ConditionalRender, DataTable, DeleteModal } from '@ui';
import {
  getRenderOPOsTableCell,
  OPOS_LIST_DEFAULT_SORT_BY,
  OPOS_LIST_FILTERS_BASE,
  OPOS_TABLE_COLUMNS,
  OPOsFilters,
} from '@lp/features/admin/tables/OPOsListTable';
import { AuthContext } from '@lp/core/auth';
import { useOpoListQuery } from '@/queries/opo';
import { useWrappedRemoveOpoMutation } from '@/queries/orchestrator';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { AdminPermissions } from '@/permissions';

export const OPOList = () => {
  const navigate = useNavigate();
  const {
    data,
    isLoading,
    isFetching,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: OPOS_LIST_FILTERS_BASE,
    query: useOpoListQuery,
    defaultSortBy: OPOS_LIST_DEFAULT_SORT_BY,
  });

  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.OpoCreate);

  const { isOpen, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    successMessage: 'Opo deleted successfully.',
    query: useWrappedRemoveOpoMutation,
  });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleCreate = () => {
    navigate('/admin/opos/locations/create');
  };

  const handleEdit = useCallback(
    (opoId: string) => {
      navigate(`/admin/opos/locations/${opoId}`);
    },
    [navigate],
  );

  const { resolveSubdivisionName } = useCountriesData();
  const renderOPOsTableCell = useMemo(() => {
    return getRenderOPOsTableCell(handleEdit, onModalOpen, resolveSubdivisionName);
  }, [handleEdit, onModalOpen, resolveSubdivisionName]);

  const OPOS_TABLE_COLUMNS_FILTERED = useMemo(() => {
    return hasCreatePermissions ? OPOS_TABLE_COLUMNS : OPOS_TABLE_COLUMNS.filter((item) => item.key !== 'actions');
  }, [hasCreatePermissions]);

  return (
    <>
      <div className="flex flex-col place-items-stretch gap-9">
        <AdminPageHeader title="OPO Locations">
          <div className="max-w-[480px] grow">
            <SearchInput placeholder="Search" value={searchText} onChange={onSearchChange} inputVariant="secondary" />
          </div>
          <ConditionalRender show={hasCreatePermissions}>
            <div className="shrink-0">
              <Button size="md" variant="solid" endContent={<MdOutlineAdd size={18} />} onClick={handleCreate}>
                Create
              </Button>
            </div>
          </ConditionalRender>
        </AdminPageHeader>
        <div className="flex flex-col gap-4">
          <OPOsFilters
            filters={filters}
            hasFilters={hasFilters}
            updateFilters={updateFilters}
            clearFilters={clearFilters}
          />
          <DataTable
            label="OPO Locations"
            columns={OPOS_TABLE_COLUMNS_FILTERED}
            renderCell={renderOPOsTableCell}
            data={data?.items || []}
            isLoading={isLoading}
            isFetching={isFetching}
            totalItems={data?.totalItems || 0}
            totalPages={data?.totalPages || 1}
            page={page}
            itemsPerPage={itemsPerPage}
            sortBy={sortBy}
            onPageChange={setPage}
            onItemsPerPageChange={setItemsPerPage}
            onSortByChange={setSortBy}
          />
        </div>
      </div>
      <DeleteModal
        title="Delete OPO?"
        description="Are you sure you want to delete the OPO?"
        isOpen={isOpen}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
